/**
 * Validation Status — Level 0 primitive. Pure, no I/O, no React.
 *
 * The canonical trust signal for a cross-validated metric is `status`
 * (verified / verified_with_variance / conflicting / insufficient /
 * single_source) — set by the backend from source-count + agreement
 * (relative MAD around the tier-weighted median). Thresholds mirror
 * `transcend-hyrule services/validation_metrics.py::aggregate`; the
 * `GET /validation/metrics` route docstring documents the same five
 * values for consumers (TRA-403 / hyrule#202, merged 2026-07-31).
 *
 * `Quality Score` is a LEGACY-COMPAT field derived FROM status (a fixed
 * base per status + a small source-count bonus, capped at 98) — it is
 * NOT an independent confidence gradient. Two metrics sharing a status
 * can carry different Quality Scores purely from the source-count bonus,
 * so banding the UI by Quality Score thresholds (the retired 90/80/70 —
 * "Investment Grade" / "High" / "Moderate" — scheme) made same-status
 * metrics render as DIFFERENT trust bands. TRA-413 retires that
 * client-side reconstruction: band by `status`; Quality Score stays
 * display-only.
 */

export const STATUS_META = {
  verified: { label: 'verified', icon: '✓', className: 'verified', color: '#10b981' },
  verified_with_variance: {
    label: 'verified with variance', icon: '~', className: 'verified-with-variance', color: '#3b82f6',
  },
  conflicting: { label: 'conflicting', icon: '✗', className: 'conflicting', color: '#ef4444' },
  insufficient: { label: 'insufficient', icon: '–', className: 'insufficient', color: '#9ca3af' },
  single_source: { label: 'single source', icon: '⚠', className: 'single', color: '#f59e0b' },
};

/**
 * Neutral, non-alarming — used when a row carries a status we don't
 * recognize. Dedicated `unknown` class (TRA-413 review fix): must NOT
 * reuse `single` — that class is single_source's amber styling, which
 * wrongly implies a trust level for a status we can't classify at all.
 */
const UNKNOWN_STATUS_META = { icon: '?', className: 'unknown', color: '#9ca3af' };

/**
 * Look up the canonical display meta (label/icon/className/color) for a
 * validated-metric's `status`. An unrecognized or missing status falls back
 * to a neutral style rather than guessing a trust level.
 *
 * @param {string} status one of the 5 canonical values from the backend
 * @returns {{label: string, icon: string, className: string, color: string}}
 */
export function getStatusMeta(status) {
  return STATUS_META[status] || { ...UNKNOWN_STATUS_META, label: status || 'unknown' };
}
